import React, { useState, useMemo } from 'react';
import { TrendingUp, DollarSign, Users, Calendar, CreditCard, AlertCircle, Download, Filter } from 'lucide-react';

// Identity for "unique customer" counts. Email is the usual join key, but a
// booking taken over the counter can have none - falling back to the phone keeps
// those from collapsing into one customer named ''.
const customerKey = (apt) => {
  const email = (apt.customer_email || '').toLowerCase().trim();
  if (email) return email;
  const phone = (apt.customer_phone || '').replace(/\D/g, '');
  return phone ? `phone:${phone}` : `apt:${apt.id}`;
};

const AdminReports = ({ appointments, services }) => {
  const [dateRange, setDateRange] = useState('year');
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  // Calculate comprehensive metrics
  const analytics = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const thisYear = new Date(now.getFullYear(), 0, 1);

    const filterByDate = (appointments, startDate, endDate = null) => {
      return appointments.filter(apt => {
        const aptDate = new Date(apt.appointment_date + 'T00:00:00');
        if (endDate) {
          return aptDate >= startDate && aptDate <= endDate;
        }
        return aptDate >= startDate;
      });
    };

    // Filter appointments based on selected date range
    let filteredAppointments = appointments;
    switch (dateRange) {
      case 'today':
        filteredAppointments = filterByDate(appointments, today);
        break;
      case 'week':
        filteredAppointments = filterByDate(appointments, thisWeek);
        break;
      case 'month':
        filteredAppointments = filterByDate(appointments, thisMonth);
        break;
      case 'year':
        filteredAppointments = filterByDate(appointments, thisYear);
        break;
      default:
        filteredAppointments = appointments;
    }

    // Revenue Analytics
    const totalRevenue = filteredAppointments
      .filter(apt => apt.payment_status === 'paid')
      .reduce((sum, apt) => sum + (apt.payment_amount || apt.final_price || apt.service_price || 0), 0);

    const pendingRevenue = filteredAppointments
      .filter(apt => apt.status === 'completed' && apt.payment_status === 'pending')
      .reduce((sum, apt) => sum + (apt.final_price || apt.service_price || 0), 0);

    const projectedRevenue = filteredAppointments
      .filter(apt => apt.status === 'scheduled')
      .reduce((sum, apt) => sum + (apt.final_price || apt.service_price || 0), 0);

    // Appointment Analytics
    const totalAppointments = filteredAppointments.length;
    const completedAppointments = filteredAppointments.filter(apt => apt.status === 'completed').length;
    const scheduledAppointments = filteredAppointments.filter(apt => apt.status === 'scheduled').length;
    const cancelledAppointments = filteredAppointments.filter(apt => apt.status === 'cancelled').length;

    // Customer Analytics
    const uniqueCustomers = new Set(
      filteredAppointments.map(customerKey)
    ).size;

    const newCustomers = filteredAppointments.filter(apt => {
      const customerAppointments = appointments.filter(a =>
        customerKey(a) === customerKey(apt)
      );
      return customerAppointments.length === 1;
    }).length;

    const returningCustomers = uniqueCustomers - newCustomers;

    // Payment Analytics
    const paidAppointments = filteredAppointments.filter(apt => apt.payment_status === 'paid').length;
    const pendingPayments = filteredAppointments.filter(apt =>
      apt.status === 'completed' && apt.payment_status === 'pending'
    ).length;

    // Payment method breakdown
    const paymentMethods = filteredAppointments
      .filter(apt => apt.payment_status === 'paid' && apt.payment_method)
      .reduce((acc, apt) => {
        const method = apt.payment_method || 'Unknown';
        acc[method] = (acc[method] || 0) + 1;
        return acc;
      }, {});

    // Service popularity
    const serviceStats = services.map(service => {
      const serviceAppointments = filteredAppointments.filter(apt =>
        apt.service_id === service.id || apt.service_name === service.name
      );
      const revenue = serviceAppointments
        .filter(apt => apt.payment_status === 'paid')
        .reduce((sum, apt) => sum + (apt.payment_amount || apt.final_price || apt.service_price || 0), 0);

      return {
        ...service,
        bookings: serviceAppointments.length,
        completed: serviceAppointments.filter(apt => apt.status === 'completed').length,
        revenue: revenue
      };
    }).sort((a, b) => b.bookings - a.bookings);

    // Monthly trends (last 12 months)
    const monthlyData = [];
    for (let i = 11; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      const monthAppointments = appointments.filter(apt => {
        const aptDate = new Date(apt.appointment_date + 'T00:00:00');
        return aptDate >= monthDate && aptDate < nextMonth;
      });

      monthlyData.push({
        month: monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        appointments: monthAppointments.length,
        revenue: monthAppointments
          .filter(apt => apt.payment_status === 'paid')
          .reduce((sum, apt) => sum + (apt.payment_amount || apt.final_price || apt.service_price || 0), 0),
        newCustomers: monthAppointments.filter(apt => {
          const customerAppointments = appointments.filter(a =>
            customerKey(a) === customerKey(apt) &&
            new Date(a.appointment_date + 'T00:00:00') <= new Date(apt.appointment_date + 'T00:00:00')
          );
          return customerAppointments.length === 1;
        }).length
      });
    }

    return {
      totalRevenue,
      pendingRevenue,
      projectedRevenue,
      totalAppointments,
      completedAppointments,
      scheduledAppointments,
      cancelledAppointments,
      uniqueCustomers,
      newCustomers,
      returningCustomers,
      paidAppointments,
      pendingPayments,
      paymentMethods,
      serviceStats,
      monthlyData,
      completionRate: totalAppointments > 0 ? (completedAppointments / totalAppointments * 100) : 0,
      avgRevenuePerAppointment: paidAppointments > 0 ? (totalRevenue / paidAppointments) : 0
    };
  }, [appointments, services, dateRange]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const exportReport = () => {
    const csvData = [
      ['Metric', 'Value'],
      ['Total Revenue', formatCurrency(analytics.totalRevenue)],
      ['Pending Revenue', formatCurrency(analytics.pendingRevenue)],
      ['Total Appointments', analytics.totalAppointments],
      ['Completed Appointments', analytics.completedAppointments],
      ['Unique Customers', analytics.uniqueCustomers],
      ['New Customers', analytics.newCustomers],
      ['Completion Rate', `${analytics.completionRate.toFixed(1)}%`],
      ['Average Revenue per Appointment', formatCurrency(analytics.avgRevenuePerAppointment)]
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `alltech-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Business Reports</h2>
            <p className="text-gray-600">Comprehensive analytics and insights</p>
          </div>

          <div className="flex gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>

            <button
              onClick={exportReport}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(analytics.totalRevenue)}</p>
              <p className="text-xs text-gray-500 mt-1">
                Avg: {formatCurrency(analytics.avgRevenuePerAppointment)}/appointment
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Appointments</p>
              <p className="text-2xl font-bold text-blue-600">{analytics.totalAppointments}</p>
              <p className="text-xs text-gray-500 mt-1">
                {analytics.completionRate.toFixed(1)}% completion rate
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Unique Customers</p>
              <p className="text-2xl font-bold text-purple-600">{analytics.uniqueCustomers}</p>
              <p className="text-xs text-gray-500 mt-1">
                {analytics.newCustomers} new customers
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Payments</p>
              <p className="text-2xl font-bold text-orange-600">{analytics.pendingPayments}</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatCurrency(analytics.pendingRevenue)} pending
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Revenue Breakdown</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Paid Revenue</span>
              <span className="font-semibold text-green-600">{formatCurrency(analytics.totalRevenue)}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Pending Payment</span>
              <span className="font-semibold text-orange-600">{formatCurrency(analytics.pendingRevenue)}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Projected (Scheduled)</span>
              <span className="font-semibold text-blue-600">{formatCurrency(analytics.projectedRevenue)}</span>
            </div>
            <div className="flex justify-between items-center py-3 font-bold">
              <span className="text-gray-800">Total Pipeline</span>
              <span className="text-gray-800">
                {formatCurrency(analytics.totalRevenue + analytics.pendingRevenue + analytics.projectedRevenue)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Appointment Status</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Completed</span>
              <span className="font-semibold text-green-600">{analytics.completedAppointments}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Scheduled</span>
              <span className="font-semibold text-blue-600">{analytics.scheduledAppointments}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Cancelled</span>
              <span className="font-semibold text-red-600">{analytics.cancelledAppointments}</span>
            </div>
            <div className="flex justify-between items-center py-3 font-bold">
              <span className="text-gray-800">Total</span>
              <span className="text-gray-800">{analytics.totalAppointments}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods & Customer Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Payment Methods</h3>
          {Object.keys(analytics.paymentMethods).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(analytics.paymentMethods).map(([method, count]) => (
                <div key={method} className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">{method}</span>
                  <span className="font-semibold text-gray-800">{count} payments</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No payment data available</p>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Customer Analytics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">New Customers</span>
              <span className="font-semibold text-green-600">{analytics.newCustomers}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Returning Customers</span>
              <span className="font-semibold text-blue-600">{analytics.returningCustomers}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Customer Retention</span>
              <span className="font-semibold text-purple-600">
                {analytics.uniqueCustomers > 0 ?
                  ((analytics.returningCustomers / analytics.uniqueCustomers) * 100).toFixed(1) : 0}%
              </span>
            </div>
            <div className="flex justify-between items-center py-3 font-bold">
              <span className="text-gray-800">Total Unique</span>
              <span className="text-gray-800">{analytics.uniqueCustomers}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Service Performance */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Service Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-2 font-semibold text-gray-700">Service</th>
                <th className="text-center py-3 px-2 font-semibold text-gray-700">Bookings</th>
                <th className="text-center py-3 px-2 font-semibold text-gray-700">Completed</th>
                <th className="text-center py-3 px-2 font-semibold text-gray-700">Revenue</th>
                <th className="text-center py-3 px-2 font-semibold text-gray-700">Avg Price</th>
              </tr>
            </thead>
            <tbody>
              {analytics.serviceStats.map(service => (
                <tr key={service.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2 font-medium text-gray-800">{service.name}</td>
                  <td className="py-3 px-2 text-center text-gray-600">{service.bookings}</td>
                  <td className="py-3 px-2 text-center text-gray-600">{service.completed}</td>
                  <td className="py-3 px-2 text-center font-semibold text-green-600">
                    {formatCurrency(service.revenue)}
                  </td>
                  <td className="py-3 px-2 text-center text-gray-600">
                    {formatCurrency(service.price)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Payment History</h3>
        <div className="overflow-x-auto max-h-96">
          <table className="w-full">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 px-2 font-semibold text-gray-700">Date</th>
                <th className="text-left py-3 px-2 font-semibold text-gray-700">Customer</th>
                <th className="text-left py-3 px-2 font-semibold text-gray-700">Service</th>
                <th className="text-center py-3 px-2 font-semibold text-gray-700">Method</th>
                <th className="text-center py-3 px-2 font-semibold text-gray-700">Amount</th>
                <th className="text-center py-3 px-2 font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {appointments
                .filter(apt => dateRange === 'all' ? true : (() => {
                  const aptDate = new Date(apt.appointment_date + 'T00:00:00');
                  const now = new Date();
                  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                  const thisYear = new Date(now.getFullYear(), 0, 1);

                  switch(dateRange) {
                    case 'today': return aptDate >= today;
                    case 'week': return aptDate >= thisWeek;
                    case 'month': return aptDate >= thisMonth;
                    case 'year': return aptDate >= thisYear;
                    default: return true;
                  }
                })())
                .filter(apt => apt.payment_status === 'paid')
                .sort((a, b) => new Date(b.payment_date || b.appointment_date) - new Date(a.payment_date || a.appointment_date))
                .map((apt, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-2 text-gray-600">
                      {new Date(apt.payment_date || apt.appointment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="py-3 px-2 font-medium text-gray-800">{apt.customer_name}</td>
                    <td className="py-3 px-2 text-gray-600">{apt.service_name}</td>
                    <td className="py-3 px-2 text-center">
                      <span className="inline-block px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                        {apt.payment_method || 'N/A'}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center font-semibold text-green-600">
                      {formatCurrency(apt.payment_amount || apt.final_price)}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className="inline-block px-2 py-1 text-xs rounded bg-green-100 text-green-800">
                        ✓ Paid
                      </span>
                    </td>
                  </tr>
                ))}
              {appointments.filter(apt => apt.payment_status === 'paid').length === 0 && (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    No payments recorded yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Monthly Trends */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Monthly Trends (Last 12 Months)</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-2 font-semibold text-gray-700">Month</th>
                <th className="text-center py-3 px-2 font-semibold text-gray-700">Appointments</th>
                <th className="text-center py-3 px-2 font-semibold text-gray-700">Revenue</th>
                <th className="text-center py-3 px-2 font-semibold text-gray-700">New Customers</th>
              </tr>
            </thead>
            <tbody>
              {analytics.monthlyData.map((month, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2 font-medium text-gray-800">{month.month}</td>
                  <td className="py-3 px-2 text-center text-gray-600">{month.appointments}</td>
                  <td className="py-3 px-2 text-center font-semibold text-green-600">
                    {formatCurrency(month.revenue)}
                  </td>
                  <td className="py-3 px-2 text-center text-gray-600">{month.newCustomers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;